import React, { useMemo } from 'react';
import { Box, Button, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { UserAvatar } from '@/components/UserAvatar';
import { FolioDrawerHeader } from '@/components/Drawers/FolioDrawer/FolioDrawerHeader';
import { FolioDrawerFormGuard } from '@/components/Drawers/FolioDrawer/FolioDrawerFormGuard';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';
import { useAdminConfirm } from '@/admin/shared/AdminConfirmProvider';
import { AdminLastUpdated } from '@/admin/shared/AdminLastUpdated';
import { useListAllBrokerGroupsQuery } from '@/admin/brokerGroups/api/brokerGroupsApi';
import { useListAllFeatureFlagsQuery } from '@/admin/featureFlags/api/featureFlagsApi';
import { useGetUserQuery, useListAllUsersQuery, useUpdateUserMutation } from '../api/usersApi';
import { getUserValidationSchema } from '../userValidationSchema';
import { uploadUserPhoto } from '../api/uploadUserPhoto';
import {
	USER_FIELD_LABELS,
	getFeatureFlagChanges,
	getUserChanges,
	getUserFormValues,
} from './userFormValues';
import { UserChangeList } from './UserChangeList';
import { UserActiveButton } from './UserActiveButton';
import { DetailsFields } from './DetailsFields';
import { BrokerGroupFields } from './BrokerGroupFields';
import { LedgerFields } from './LedgerFields';
import { PermissionFields } from './PermissionFields';
import { FeatureFlagFields } from './FeatureFlagFields';
import { PhotoField } from './PhotoField';
import { useApplyFeatureFlagChanges } from './useApplyFeatureFlagChanges';

// Every field starts touched, so data that's already invalid (e.g. broker groups on
// different ledgers) shows its error as soon as the drawer opens
const INITIAL_TOUCHED = Object.fromEntries(
	Object.keys(USER_FIELD_LABELS).map((field) => [field, true]),
);

/**
 * Edits one user (getUser, filled from the cached users list) with Formik and
 * getUserValidationSchema. Save lists each change for the admin to confirm, uploads a new
 * photo (uploadUserPhoto), then sends only the changed fields through updateUser, then adds
 * or removes the user on each feature flag that changed, and closes. Errors the API ties to
 * a field show under it and keep the drawer open; others, and flags that fail, show a toast.
 */
export const UserForm = ({ userId, onClose }) => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const [updateUser] = useUpdateUserMutation();
	const applyFeatureFlagChanges = useApplyFeatureFlagChanges();
	const { data: user, isLoading } = useGetUserQuery(userId);
	const { data: brokerGroups, isLoading: areBrokerGroupsLoading } = useListAllBrokerGroupsQuery();
	const { data: flags, isLoading: areFlagsLoading } = useListAllFeatureFlagsQuery();

	const { data: users, isLoading: areUsersLoading } = useListAllUsersQuery();

	const validationSchema = useMemo(
		() =>
			brokerGroups &&
			users &&
			user &&
			getUserValidationSchema(
				new Map(brokerGroups.map((brokerGroup) => [brokerGroup.id, brokerGroup])),
				users,
				// An existing Folio admin keeps their access, whatever their email
				{ userId, wasFolioAdmin: Boolean(user.isFolioAdmin) },
			),
		[brokerGroups, users, user, userId],
	);
	const initialValues = useMemo(
		() => user && flags && getUserFormValues(user, flags),
		[user, flags],
	);

	if (isLoading || areBrokerGroupsLoading || areFlagsLoading || areUsersLoading) {
		return (
			<Box display='flex' justifyContent='center' py={4}>
				<CircularProgress />
			</Box>
		);
	}
	if (!user || !brokerGroups || !flags || !users) {
		return (
			<>
				<FolioDrawerHeader title='User' />
				<Typography sx={{ p: 2 }}>Could not load this user.</Typography>
			</>
		);
	}

	const handleSubmit = async (values, { setErrors }) => {
		const { changed, before, after, body } = getUserChanges(
			validationSchema,
			initialValues,
			values,
			user,
		);
		const featureFlagChanges = getFeatureFlagChanges(initialValues, values);

		const confirmed = await confirm({
			title: `Update ${user.name}?`,
			content: (
				<UserChangeList
					changed={changed}
					before={before}
					after={after}
					featureFlagChanges={featureFlagChanges}
				/>
			),
			confirmText: 'Save',
		});
		if (!confirmed) return;

		if (values.photoBlob) {
			try {
				body.imageUrl = await uploadUserPhoto(userId, values.photoBlob);
			} catch (error) {
				console.error('Could not upload the photo', error);
				dispatch(
					showToast({
						message: `Couldn't upload ${user.name}'s photo. Please try again.`,
						error: true,
					}),
				);
				return;
			}
		}

		if (Object.keys(body).length) {
			const result = await updateUser({ id: userId, ...body });
			if (result.error) {
				const fields = result.error.data?.fields;
				if (fields) {
					setErrors(fields);
					return;
				}
				dispatch(
					showToast({
						message: `Couldn't update ${user.name}. Please try again.`,
						error: true,
					}),
				);
				return;
			}
		}

		// The user is saved first, so a flag that fails can be retried by saving again (the
		// user's own fields then don't change)
		const failedFlags = await applyFeatureFlagChanges(userId, featureFlagChanges);
		if (failedFlags.length) {
			dispatch(
				showToast({
					message: `Couldn't update these feature flags: ${failedFlags.join(', ')}. Please try again.`,
					error: true,
				}),
			);
			return;
		}
		dispatch(showToast({ message: 'Changes saved successfully.', success: true }));
		onClose();
	};

	return (
		<Stack sx={{ flex: 1, minHeight: 0, height: '100%', overflow: 'hidden', pt: 1 }}>
			<FolioDrawerHeader
				title={user.name}
				icon={<UserAvatar userId={userId} width={48} height={48} fontSize='1.25em' />}
				headerActions={<UserActiveButton user={user} />}
			/>
			<Formik
				initialValues={initialValues}
				initialTouched={INITIAL_TOUCHED}
				validationSchema={validationSchema}
				validateOnMount
				onSubmit={handleSubmit}
			>
				{({ dirty, isValid, isSubmitting }) => (
					<Form
						style={{
							display: 'flex',
							flexDirection: 'column',
							flex: 1,
							minHeight: 0,
							width: '100%',
							height: '100%',
							overflow: 'hidden',
						}}
					>
						<Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
							<Grid container spacing={2}>
								<PhotoField />
								<DetailsFields />
								<BrokerGroupFields />
								<LedgerFields />
								<PermissionFields userId={userId} />
								<FeatureFlagFields
									userId={userId}
									brokerGroupIds={user.brokerGroups.map(({ id }) => id)}
									ledger={user.ledger}
								/>
							</Grid>
						</Box>
						<AdminLastUpdated {...user} />
						<FolioDrawerFormGuard />
						<FolioDrawerFooter>
							<Button
								type='submit'
								variant='contained'
								disabled={!dirty || !isValid || isSubmitting}
							>
								Save
							</Button>
						</FolioDrawerFooter>
					</Form>
				)}
			</Formik>
		</Stack>
	);
};

UserForm.displayName = 'UserForm';
